import { useEffect, useRef } from "react";
import { scrollHeaderIntoView } from "./utils/scrollToHeader";
import { useTheme } from "../context/ThemeContext";

const GOLD = "#AC8E66";

const SERVICES = [
  {
    id: "01",
    label: "Entwicklung",
    desc: "React · Angular · Flutter · System Architecture",
  },
  {
    id: "02",
    label: "Training",
    desc: "Workshops, Kurse & Team-Enablement",
  },
  {
    id: "03",
    label: "Beratung",
    desc: "Architektur-Reviews · Code-Audits · Strategie",
  },
];

export default function ServiceSection({ onToggle, isOpen = false, headerRef, autoScrollOnOpen = true }) {
  const { isDark } = useTheme();
  const BG = isDark ? "#1A1A1A" : "#d0cbb8";
  const TEXT = isDark ? "#e8e0d4" : "#1a1a1a";
  const MUTED = isDark ? "#b0ac9b" : "#2a2a2a";
  const BORDER = isDark ? "#2a2a2a" : "rgba(26,26,26,0.3)";
  const ACCENT = isDark ? GOLD : "#1a1a1a";
  const localHeaderRef = useRef(null);
  const resolvedHeaderRef = headerRef || localHeaderRef;

  useEffect(() => {
    if (!autoScrollOnOpen || !isOpen || !resolvedHeaderRef.current) return;
    scrollHeaderIntoView(resolvedHeaderRef.current, "smooth", 8);
  }, [autoScrollOnOpen, isOpen, resolvedHeaderRef]);

  return (
    <div
      ref={resolvedHeaderRef}
      style={{
        background: BG,
        borderTop: `1px solid ${BORDER}`,
        borderBottom: `1px solid ${BORDER}`,
        fontFamily:
          'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
        position: "relative",
        cursor: "pointer",
        scrollMarginTop: "calc(var(--navbar-height, 0px) + 12px)",
      }}
      className="service-section"
      role="button"
      tabIndex={0}
      aria-expanded={isOpen}
      onClick={onToggle}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") onToggle?.();
      }}
    >
      {/* Pfeil – immer ganz rechts, außerhalb des Grids */}
      <span
        style={{
          position: "absolute",
          top: "50%",
          right: "24px",
          color: ACCENT,
          fontSize: "1rem",
          lineHeight: 1,
          display: "inline-block",
          pointerEvents: "none",
          transform: isOpen
            ? "translateY(-50%) rotate(90deg)"
            : "translateY(-50%) rotate(0deg)",
          transition: "transform 0.3s ease",
        }}
      >
        →
      </span>

      {/* Content grid */}
      <div
        style={{
          maxWidth: "1100px",
          margin: "0 auto",
          padding: "48px 64px 48px 32px",
          display: "grid",
          gridTemplateColumns: isOpen ? "1fr" : "1fr 1fr",
          gap: "80px",
          alignItems: "center",
        }}
        className="service-section-grid"
      >
        {/* Links: Label + Headline */}
        <div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              marginBottom: "20px",
            }}
          >
            <span
              style={{
                border: `1px solid ${ACCENT}`,
                color: ACCENT,
                fontSize: "0.6rem",
                padding: "2px 8px",
                letterSpacing: "0.14em",
                textTransform: "uppercase",
              }}
            >
              {"// Services"}
            </span>
          </div>

          <h2
            style={{
              color: TEXT,
              fontSize: "clamp(1.2rem, 2.5vw, 1.8rem)",
              fontWeight: 700,
              lineHeight: 1.2,
              margin: "0 0 16px 0",
            }}
          >
            Software, Training,{" "}
            <span style={{ color: ACCENT }}>Beratung</span>
          </h2>

          <div style={{ display: "flex", gap: "20px", marginTop: "24px" }}>
            {[
              { value: "250+", label: "Trainings" },
              { value: "50+", label: "Projekte" },
              { value: "10+", label: "Jahre" },
            ].map((m) => (
              <div key={m.label}>
                <span style={{ display: "block", color: ACCENT, fontSize: "0.9rem", fontWeight: 700 }}>
                  {m.value}
                </span>
                <span style={{ color: MUTED, fontSize: "0.6rem", letterSpacing: "0.14em", textTransform: "uppercase" }}>
                  {m.label}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Rechts – nur als Teaser wenn collapsed */}
        {!isOpen && (
          <div>
            {SERVICES.map((s) => (
              <div
                key={s.id}
                style={{
                  display: "flex",
                  alignItems: "baseline",
                  gap: "20px",
                  padding: "16px 0",
                  borderBottom: `1px solid ${BORDER}`,
                }}
              >
                <span
                  style={{
                    color: ACCENT,
                    fontSize: "0.6rem",
                    letterSpacing: "0.15em",
                    minWidth: "24px",
                    flexShrink: 0,
                  }}
                >
                  {s.id}
                </span>
                <div>
                  <div
                    style={{
                      color: TEXT,
                      fontSize: "0.88rem",
                      fontWeight: 600,
                      marginBottom: "4px",
                    }}
                  >
                    {s.label}
                  </div>
                  <div style={{ color: MUTED, fontSize: "0.75rem" }}>{s.desc}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <style>{`
        @media (max-width: 768px) {
          .service-section-grid {
            grid-template-columns: 1fr !important;
            gap: 32px !important;
            padding-top: 40px !important;
            padding-bottom: 40px !important;
          }
        }
        .service-section:hover h2 {
          color: ${ACCENT};
          transition: color 0.2s;
        }
      `}</style>
    </div>
  );
}
